import { Injectable, NgZone } from '@angular/core';
import { tap, shareReplay, take } from 'rxjs/operators';
import { StorageService } from './storage.service';
import { SicorApiService } from '../SicorService/sicor-api.service';
import { GlobalVariablesService } from '../global-variables.service';
import { NavigationService } from './navigation.service';
import { environment } from 'src/environments/environment';
import { TranslateConfigService } from 'src/app/services/commons/translate-config.service';
import { GlobalMethodsService } from '../global-methods.service';
import { UserLoginRequest } from '../SicorService/RequestObject/UserLoginRequest';
import { FingerprintAccessComponent } from '../../components/modals/fingerprint-access/fingerprint-access.component';
import { ModalController, Platform } from '@ionic/angular';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { DeviceService } from './device.service';
import { PanelConfService } from './panel-conf.service';
import { OpenNativeSettings } from '@ionic-native/open-native-settings/ngx';
import { LogOutRequest } from '../SicorService/RequestObject/LogOutRequest';
import { CheckDealerRequest } from '../SicorService/RequestObject/CheckDealerRequest';
import { VersionControlRequest } from '../SicorService/RequestObject/VersionControlRequest';
import { VersionControlResponse } from '../SicorService/ResponseObject/VersionControlResponse';
import { VersionControlService } from './version-control.service';
import { LoginProblemsComponent } from 'src/app/components/modals/login-problems/login-problems.component';
import { EMPTY, firstValueFrom } from 'rxjs';
import { PasswordUpdateResponse } from '../SicorService/ResponseObject/PasswordUpdateResponse';
import { FirebaseCrashlytics } from '@capacitor-community/firebase-crashlytics';
import { Installation } from '../../models/Installation';
import { TimerService } from './timer.service';
import { EventNotificationService } from './event-notification.service';
import { UserLogin } from 'src/app/models/UserLogin';
const UNIQUE_DEVICE_ID = 'unique-device-id';
@Injectable({
  providedIn: 'root'
})
export class AuthService {

  constructor(private storage: StorageService,
    private sicorAPI: SicorApiService,
    public gvs: GlobalVariablesService,
    private navigationService: NavigationService,
    private translate: TranslateConfigService,
    public gms: GlobalMethodsService,
    private navigation: NavigationService,
    private zone: NgZone,
    private modalCtrl: ModalController,
    private deviceService: DeviceService,
    public platform: Platform,
    private panelConfService: PanelConfService,
    private openNativeSettings: OpenNativeSettings,
    private versionControlService: VersionControlService,
    private timerService: TimerService,
    private eventNotification: EventNotificationService
  ) { }

  public async auth(credentials: UserLoginRequest, checkSession: boolean): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.userLogAccess(credentials)).pipe(
        take(1),
        tap({
          next: async res => {
            if (res.expiration_seconds) {
              const validUntil = new Date();
              validUntil.setSeconds(res.expiration_seconds);
              await this.storage.setObject(this.gvs.TOKEN_EXPIRATION_KEY, validUntil);
            }
            if (res.result) {
              this.gms.analyticsTrackEvent('login', { eventCategory: 'SICOR/Login', eventAction: 'Accion/Login', sign_up_method: 'email' });
              const token = res.result;
              await this.storage.setItem(this.gvs.TOKEN_KEY, token);
              await this.setUsersLogin(credentials, checkSession);
              await this.setUserLogin(credentials);
              let checkDealer = await this.checkDealer(credentials.userName);
              if (checkDealer != null) {
                if (checkDealer[0] != null && checkDealer[0].error != null) {
                  if (checkDealer[0].error === 0) {
                    if(checkDealer[0].nps_view != null){
                      await this.storage.setItem(this.gvs.NPS_VIEW, checkDealer[0].nps_view.toString());
                    }
                    let device_id = await this.getDeviceId();
                    let checkUpdateDevice = true;
                    if (await this.platform.is('cordova')) {
                      if (device_id != null && device_id !== "") {
                        checkUpdateDevice = await this.deviceService.sendDevice(device_id);
                      }
                    } if (checkUpdateDevice == null || !checkUpdateDevice) {
                      this.modalCtrl.dismiss(false, '', 'loadSpinner');
                      await this.navigation.logout();
                      await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleError'), await this.translate.getTranslate('MESSAGES.resourceNotAvailable'),
                        'modal-default', await this.translate.getTranslate('BUTTON.agreed'));
                      resolve(false);
                    } else {
                      const installations = await this.panelConfService.getInstallations(credentials.userName, this.gvs.lang);
                      if (installations == null) {
                        await this.openFailedInstallationsModal();
                        await this.navigationService.logout(false);
                        resolve(false);
                      } else if (installations.length === 0) {
                        await this.openNotAccessInstallationsModal();
                        await this.navigationService.logout(false);
                        resolve(false);
                      } else {
                        installations.forEach(installation => {
                          if (installation.timer_flg != null && installation.timer_flg === 1) {
                            this.timerService.timerCyberSafeguardEvent({ status: true, seconds: installation.timer_count, minutes: installation.timer_set });
                            this.timerService.counterSins = installation.s_ins;
                            this.gms.setStatusTimerPanel(true);
                          }
                        })
                        await this.panelConfService.getCardsNews(credentials.userName, this.gvs.currentInstallation.s_ins);
                        await this.gms.checkRatingTime();
                        resolve(true);
                      }
                    }
                  } else if (checkDealer[0].error === 401) {
                    await this.navigation.logout();
                    await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleError'), await this.translate.getTranslate('MESSAGES.resourceNotAvailable'),
                      'modal-default', await this.translate.getTranslate('BUTTON.agreed'));
                    resolve(false);
                  }
                  else {
                    await this.navigation.logout();
                    await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleError'), await this.translate.getTranslate('MESSAGES.errorServer')
                      , 'modal-default', await this.translate.getTranslate('BUTTON.agreed'));
                    resolve(false);
                  }
                }
              } else {
                await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleError'), checkDealer != null ? checkDealer[0].msg : await this.translate.getTranslate('MESSAGES.errorServer'),
                  'modal-default', await this.translate.getTranslate('BUTTON.agreed'));
                await this.navigationService.logout();
                resolve(false);
              }
            } else {
              this.gms.analyticsTrackEvent('login_error', { eventCategory: 'SICOR/Login', eventAction: 'Error/Login', eventLabel: 'Error/AuthenticationError' });
              await this.openFailedLoginModal();
              resolve(false);
            }
          }, error: async e => {
            this.gms.analyticsTrackEvent('login_error', { eventCategory: 'SICOR/Login', eventAction: 'Error/Login', eventLabel: 'Error/InternalServerError' });
            await this.openFailedLoginModal();
            resolve(false);
          }
        })
      ));
    });
  }

  public async updateToken(credentials: UserLoginRequest, checkSession: boolean): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      let token: string = await this.sicorAPI.getAuthToken(credentials, '', new Date());
      if (token != null) {
        resolve(true);
      } else {
        resolve(false);
      }
    })
  }

  // Necesario para actualizar el s_ins de la ultima instalacion seleccionada del app_name correspondiente
  // y que el pc num que se devuelva en el token coincida con esa instalación.
  async authPasscard(credentials: UserLoginRequest): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      let token: string = await this.sicorAPI.getAuthToken(credentials, '', new Date());
      if (token != null) {
        await this.storage.setObject(this.gvs.AUTH_KEY, credentials);
        const response = await this.panelConfService.setLastIns(0, credentials.userName, 0);
        if (response) {
          resolve(true);
        } else {
          resolve(false);
        }
      }else{
        await this.openFailedLoginModal();
        resolve(false);
      }
    })
  }

  public async sendPasswordResetSMS(email: string, token: string): Promise<PasswordUpdateResponse> {
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.requestNewPassSms(email, token)).pipe(
        take(1),
        tap({
          next: async res => {
            if (res.resultSet != null && res.resultSet[0] != null && res.resultSet[0].error != null) {
              const passwordUpdateResponse: PasswordUpdateResponse = new PasswordUpdateResponse(res.resultSet[0].error, res.resultSet[0].message);
              resolve(passwordUpdateResponse);
            } else {
              const passwordUpdateResponse: PasswordUpdateResponse = new PasswordUpdateResponse(500, '');
              resolve(passwordUpdateResponse);
            }

          },
          error: async e => {
            const passwordUpdateResponse: PasswordUpdateResponse = new PasswordUpdateResponse(500, '');
            resolve(passwordUpdateResponse);
          }
        })
      ))
    });
  }

  public async updateCredentials(updateCredentials: any, token): Promise<PasswordUpdateResponse> {
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.requestPasswordUpdate(updateCredentials, token)).pipe(
        take(1),
        tap({
          next:
            async res => {
              if (res.resultSet != null && res.resultSet[0] != null) {
                const passwordUpdateResponse: PasswordUpdateResponse = new PasswordUpdateResponse(res.resultSet[0].error, res.resultSet[0].message);
                resolve(passwordUpdateResponse);
              } else {
                const passwordUpdateResponse: PasswordUpdateResponse = new PasswordUpdateResponse(500, '');
                resolve(passwordUpdateResponse);
              }
            }, error: e => {
              this.gms.analyticsTrackEvent('pasword_forgotten', { eventCategory: 'SICOR/Reset Password', eventAction: 'Error/Reset Password', eventLabel: 'Error/InternalServerError' });
              const passwordUpdateResponse: PasswordUpdateResponse = new PasswordUpdateResponse(500, '');
              resolve(passwordUpdateResponse);
            }
        })
      ));
    });
  }

  public serializeAuthCredentials(userName, password): UserLoginRequest {
    return {
      userName,
      password,
      type: 'EMAIL',
    };
  }

  public async getTokenDefaultAuth(): Promise<string> {
    const paramsRequest: UserLoginRequest = new UserLoginRequest('', '', 'PREAUTH');
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.userLogAccess(paramsRequest)).pipe(
        take(1),
        tap({
          next: async res => {
            if (res.result) {
              const token = res.result;
              await this.storage.setItem(this.gvs.TOKEN_KEY, token);
              resolve(token);
            }
          }, error: async e => {
            await this.storage.setItem(this.gvs.TOKEN_KEY, null);
            resolve(null);
          }
        })
      ));
    });
  }

  public async checkLogged() {
    const token = await this.storage.getItem(this.gvs.TOKEN_KEY);
    const validUntil: Date = await this.storage.getObject(this.gvs.TOKEN_EXPIRATION_KEY);
    if (!token || new Date(validUntil) < new Date()) {
      await (await this.defaultAuth());
    }
  }

  public async defaultAuth() {
    const userLoginRequest: UserLoginRequest = new UserLoginRequest('', '', 'PREAUTH');
    try {
      await this.auth(userLoginRequest, false);
    } catch (e) {
      const message = JSON.stringify(e);
      await FirebaseCrashlytics.recordException({ message: message });
    }
  }

  public async ifAuthLoggedIn(): Promise<boolean> {
    const user = await this.storage.getObject(this.gvs.AUTH_KEY);
    if (user != null && user.userName !== '' && user.type !== 'PREAUTH') {
      return true;
    } else {
      return false;
    }
  }

  // public async getRecoveryEmail() {
  //   const recoveryEmail = await this.storage.getItem(this.gvs.RECOVERY_EMAIL);
  //   if (recoveryEmail && recoveryEmail !== environment.defaultUser) {
  //     return recoveryEmail;
  //   }
  //   return '';
  // }

  async isExpirationKey() {
    const validUntil: Date = await this.storage.getObject(this.gvs.TOKEN_EXPIRATION_KEY);
    if (new Date(validUntil) < new Date()) {
      await this.defaultAuth();
    }
  }

  public async getDeviceId() {
    let deviceID = await this.storage.getItem(UNIQUE_DEVICE_ID);

    if (!deviceID) {
      return null;
    }
    return deviceID;
  }

  public async setDeviceId(deviceId: string) {
    await this.storage.setItem(UNIQUE_DEVICE_ID, deviceId);
  }

  async setExpirationAuth() {
    let expirationAuth = await this.storage.getObject(this.gvs.EXPIRATION_AUTH);
    expirationAuth = new Date();
    expirationAuth.setMinutes(expirationAuth.getMinutes() + 10);
    await this.storage.setObject(this.gvs.EXPIRATION_AUTH, expirationAuth);
  }

  private async openFailedLoginModal() {
    const title = await this.translate.getTranslate('MODAL.titleError');
    const text = await this.translate.getTranslate('MESSAGES.errorLogin');
    const mainButton = await this.translate.getTranslate('BUTTON.agreed');
    const cssClass = 'modal-default';
    await this.gms.openGenericModal(title, text, cssClass, mainButton);
  }

  async openSMSMessageSentModalOK() {
    const title = await this.translate.getTranslate('LOGIN.messageSent');
    const text = await this.translate.getTranslate('MESSAGES.followSMS');
    const mainButton = await this.translate.getTranslate('BUTTON.agreed');
    const cssClass = 'modal-default';
    await this.gms.openGenericModal(title, text, cssClass, mainButton);
  }

  async openSMSMessageSentModalKO(message: string) {
    const title = await this.translate.getTranslate('MODAL.titleInfo');
    const mainButton = await this.translate.getTranslate('BUTTON.agreed');
    const cssClass = 'modal-default';
    await this.gms.openGenericModal(title, message, cssClass, mainButton);
  }

  async openSuccessRestorePasswordModal() {
    const title = await this.translate.getTranslate('MODAL.titleInfo');
    const text = await this.translate.getTranslate('MESSAGES.passModified');
    const mainButton = await this.translate.getTranslate('BUTTON.agreed');
    const cssClass = 'modal-default';
    return await this.gms.openGenericModal(title, text, cssClass, mainButton);
  }

  async openSuccessSetPasswordModal() {
    const title = await this.translate.getTranslate('MODAL.titleInfo');
    const text = await this.translate.getTranslate('MESSAGES.passSetOk');
    const mainButton = await this.translate.getTranslate('BUTTON.agreed');
    const cssClass = 'modal-default';
    return await this.gms.openGenericModal(title, text, cssClass, mainButton);
  }

  async openRestartProcessModal() {
    const title = await this.translate.getTranslate('MODAL.titleError');
    const text = await this.translate.getTranslate('MESSAGES.restartProcess');
    const mainButton = await this.translate.getTranslate('BUTTON.accept');
    const cssClass = 'modal-default';
    await this.gms.openGenericModal(title, text, cssClass, mainButton);
  }

  private async openFailedInstallationsModal() {
    const title = await this.translate.getTranslate('MODAL.titleError');
    const text = await this.translate.getTranslate('MESSAGES.notFoundInstallationsList');
    const mainButton = await this.translate.getTranslate('BUTTON.agreed');
    const cssClass = 'modal-default';
    await this.gms.openGenericModal(title, text, cssClass, mainButton);
  }

  private async openNotAccessInstallationsModal() {
    const title = await this.translate.getTranslate('MODAL.titleError');
    const text = await this.translate.getTranslate('MESSAGES.notAccessInstallations');
    const mainButton = await this.translate.getTranslate('BUTTON.agreed');
    const cssClass = 'modal-default';
    await this.gms.openGenericModal(title, text, cssClass, mainButton);
  }

  async openChangePasswordModal(text: string) {
    const title = await this.translate.getTranslate('MODAL.titleInfo');
    const mainButton = await this.translate.getTranslate('BUTTON.agreed');
    const cssClass = 'modal-default';
    return await this.gms.openGenericModal(title, text, cssClass, mainButton);
  }

  async fingerprintAccess(btnFingerPrint: boolean) {
    const firstUse = await this.storage.getObject('FIRST_USE');
    const user = this.gvs.user;
    const userLogin = await this.getUserLogin(user);
    const contentModal = await this.modalCtrl.create({
      component: FingerprintAccessComponent,
      componentProps: { type: 'faio', firstUse: firstUse, activeBiometric: userLogin.activeBiometric },
      cssClass: [firstUse === 1 ? 'w-modal-md' : 'modal-hidden'],
    });

    contentModal.onDidDismiss().then(result => {
      this.zone.run(async () => {
        const user = await this.storage.getObject(this.gvs.AUTH_KEY);
        if (result.data.confirm != null && result.data.confirm && result.data.msgFingerPrint != null) {
          if (result.data.msgFingerPrint === 'biometric_success' || result.data.msgFingerPrint === 'Success') {
            if (user != null && user.userName != null && user.password != null) {
              if (!btnFingerPrint) {
                await this.setExpirationAuth();
              }
              await this.setBiometricLogin(user.userName, true);
              await this.goHome()
            }
          } else if (result.data.msgFingerPrint === -106) {
            const response = await this.gms.openSettingsDevice();
            if (response) {
              await this.openNativeSettings.open('settings');
            } else {
              if (user != null && !btnFingerPrint) {
                await this.goHome();
              }
            }
          } else if (result.data.msgFingerPrint === -102) {
            await this.gms.openFailedBiometricLoginModal();
          } else if (result.data.msgFingerPrint === -100 || result.data.msgFingerPrint === -101 || result.data.msgFingerPrint === -103 || result.data.msgFingerPrint === -104 || result.data.msgFingerPrint === 'cordova_not_available') {
            await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleInfo'), await this.translate.getTranslate('MESSAGES.biometricHardwNotSupport'),
              'modal-default', await this.translate.getTranslate('BUTTON.agreed'));
          }
          else if (result.data.msgFingerPrint === -108) {
            if (user != null && !btnFingerPrint) {
              await this.goHome();
            }
          } else if (result.data.msgFingerPrint === -112) {
            await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleInfo'), await this.translate.getTranslate('MESSAGES.bioLockedOutPermanent'),
              'modal-default', await this.translate.getTranslate('BUTTON.agreed'));
          }
          else {
            await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleInfo'), await this.translate.getTranslate('MESSAGES.fingerprintDisabled'),
              'modal-default', await this.translate.getTranslate('BUTTON.agreed'));
          }
        } else if (result.data.confirm != null && !result.data.confirm) {
          const modal = await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleInfo'), await this.translate.getTranslate('MESSAGES.accessBiometricNotActivated'),
            'modal-default', await this.translate.getTranslate('BUTTON.agreed'));
          modal.onDidDismiss().then(response => {
            this.zone.run(async () => {
              if (!btnFingerPrint) {
                await this.goHome();
              }
              await this.setBiometricLogin(user, false);
            });
          });
        }
      });
    });

    contentModal.present().then();
  }

  async logOut() {
    const id_device = await this.getDeviceId();
    if (id_device == null) {
      return false;
    }
    const logOutRequest: LogOutRequest = new LogOutRequest(this.gvs.user.userName, id_device);

    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.logOut(logOutRequest)).pipe(
        take(1),
        tap({
          next: async res => {
            if (res.resultSet[0] != null && res.resultSet[0].error != null && res.resultSet[0].error === 0) {
              resolve(true);
            }
            else {
              resolve(false);
            }
          },
          error: async e => {
            resolve(false);
          }
        })
      ))
    });
  }

  public async checkDealer(user: string): Promise<any> {
    let paramsRequest: CheckDealerRequest = new CheckDealerRequest(user, 'SIC');
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.checkDealerRequest(paramsRequest)).pipe(
        take(1),
        tap({
          next: async res => {
            resolve(res.resultSet);
          }, error: async e => {
            resolve(null);
          }
        })
      ))
    });
  }

  async checkVersionApp(credentials: UserLoginRequest, token: string, lang: string): Promise<boolean> {
    let platform: string;
    if (this.platform.is('cordova')) {
      if (this.platform.is('android')) {
        platform = 'android';
      } else {
        platform = 'ios';
      }
    } else {
      platform = 'web';
    }
    let versionControlRequest: VersionControlRequest = new VersionControlRequest(environment.APP_VERSION, platform, 'SIC', credentials.userName, credentials.password);
    const response: VersionControlResponse = await this.versionControlService.checkVersionApp(versionControlRequest, token);
    return new Promise(async (resolve, reject) => {
      if (response.error === 303) {
        if (response.message !== '') {
          const modal = await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleInfo'), response.message, 'modal-default',
            response.lockapp ? await this.translate.getTranslate('BUTTON.agreed') : await this.translate.getTranslate('BUTTON.updateApp'),
            response.lockapp ? '' : await this.translate.getTranslate('BUTTON.cancel'));
          modal.onDidDismiss().then(async res => {
            if (res.data != null && res.data.result != null && res.data.result) {
              this.modalCtrl.dismiss(false, '', 'loadSpinner');
              if (this.platform.is('cordova')) {
                this.gms.goToAppStore(response.urlstore)
                this.navigation.closeApp();
              }
              resolve(false);
            } else {
              if (response.lockapp) {
                this.modalCtrl.dismiss(false, '', 'loadSpinner');
                await this.navigation.closeApp();
                resolve(false);
              } else {
                await this.storage.setItem(this.gvs.VIDEO_LIVE_TIME, response.videoLiveTime.toString());
                resolve(true);
              }
            }
          });
        } else {
          await this.storage.setItem(this.gvs.VIDEO_LIVE_TIME, response.videoLiveTime.toString());
          resolve(true);
        }
      } else if (response.error === 301) {
        const modal = await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleInfo'), response.message, 'modal-default',
          await this.translate.getTranslate('BUTTON.agreed'));
        const titleModal = await this.translate.getTranslate('LOGIN.loginProblems.helpRecoveryPassword');
        modal.onDidDismiss().then(async res => {
          const contentModal = await this.modalCtrl.create({
            component: LoginProblemsComponent,
            id: "loginProblems",
            componentProps: { title: titleModal },
            cssClass: ['w-modal-fp'],
          });
          contentModal.present().then();
          resolve(false);
        });
      } else if (response.error === 302) {
        await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleInfo'), response.message, 'modal-default',
          await this.translate.getTranslate('BUTTON.agreed'));
        resolve(false);
      } else if (response.error === 200) {
        await this.storage.setItem(this.gvs.VIDEO_LIVE_TIME, response.videoLiveTime.toString());
        resolve(true);
      } else {
        resolve(false);
      }
    });
  }

  async createTokenWeb() {
    let currentDate = new Date();
    let newDate = new Date(currentDate.getTime());
    newDate.setHours(newDate.getHours() + 4);
    let tokenWeb = await this.storage.getObject('TOKEN_WEB');
    tokenWeb = tokenWeb != null ? tokenWeb : newDate.getTime();
    if (currentDate.getTime() >= tokenWeb) {
      this.deleteTokenWeb();
      await this.navigationService.logout();
    } else {
      let newTime = currentDate;
      newTime.setHours(newTime.getHours() + 4);
      tokenWeb = newTime.getTime();
      await this.storage.setObject('TOKEN_WEB', tokenWeb);
    }
  }

  async startCronTokenWeb() {
    if (this.gvs.cronTokenWeb === 0) {
      await this.createTokenWeb();
      this.gvs.cronTokenWeb = window.setInterval(async () => {
        await this.createTokenWeb();
      }, 900000);
    }
  }

  async updateTokenWeb(){
    let currentDate = new Date();
    let newDate = new Date(currentDate.getTime());
    newDate.setHours(newDate.getHours() + 4);
    await this.storage.setObject('TOKEN_WEB', newDate);
  }

  deleteTokenWeb() {
    window.clearInterval(this.gvs.cronTokenWeb);
    this.gvs.cronTokenWeb = 0;
  }

  async goHome() {
    if (this.gvs.notificationData != null) {
      if (this.gvs.notificationData.screen === 'SVRQ' || this.gvs.notificationData.screen === 'BURGLARY') {
        await this.navigation.goHome();
      } else {
        this.eventNotification.$redirectNotifications.next(true);
      }
    } else {
      await this.navigation.goHome();
    }
  }

  async setUserLogin(credentials: UserLoginRequest) {
    const usersLogin: UserLogin[] = await this.storage.getObject(this.gvs.USERS_LOGIN);
    const userLogin: UserLogin = usersLogin.find(x => x.userName === credentials.userName);
    this.gvs.user = userLogin;
    await this.storage.setObject(this.gvs.AUTH_KEY, userLogin);
  }

  async setUsersLogin(credentials: UserLoginRequest, checkSession: boolean) {
    let users: UserLogin[] = await this.storage.getObject(this.gvs.USERS_LOGIN);
    let tempUsers: UserLogin[] = [];
    users = users != null && users.length > 0 ? users : tempUsers;
    const tempUserLogin: UserLogin = new UserLogin(credentials.userName, credentials.password, credentials.type, false, checkSession);
    if (users.length > 0) {
      if (users.find(x => x.userName === credentials.userName) == null) {
        users.push(tempUserLogin);
      } else {
        for (let i = 0; i < users.length; i++) {
          if (users[i].userName === credentials.userName) {
            users[i].password = credentials.password;
            users[i].checkSession = checkSession;
          }
        }
      }
    } else {
      users.push(tempUserLogin);
    }
    await this.storage.setObject(this.gvs.USERS_LOGIN, users);
  }

  async getUsersLogin(): Promise<UserLogin[]> {
    return await this.storage.getObject(this.gvs.USERS_LOGIN);
  }

  async getUserLogin(user: UserLoginRequest): Promise<UserLogin> {
    const usersLogin: UserLogin[] = await this.storage.getObject(this.gvs.USERS_LOGIN);
    return usersLogin.find(x => x.userName === user.userName);
  }

  async setBiometricLogin(user: string, activeBiometric: boolean) {
    let usersLogin: UserLogin[] = await this.storage.getObject(this.gvs.USERS_LOGIN);
    for (let i = 0; i < usersLogin.length; i++) {
      if (usersLogin[i].userName === user) {
        usersLogin[i].activeBiometric = activeBiometric;
        await this.storage.setObject(this.gvs.AUTH_KEY, usersLogin[i]);
      }
    }
    await this.storage.setObject(this.gvs.USERS_LOGIN, usersLogin);
  }

}
